import { ArrowUturnLeftIcon } from "@heroicons/react/16/solid";
import { ArrowDownTrayIcon } from "@heroicons/react/20/solid";
import { TrashIcon } from "@heroicons/react/24/outline";
import { useQueryClient } from "@tanstack/react-query";
import ButtonWithPopoverDots from "components/buttons/ButtonWithPopoverDots";
import { t } from "i18next";
import type { FunctionComponent } from "react";
import { useCallback, useMemo, useState } from "react";

import { getValidationMessage } from "~/api/axiosUtils.ts";
import BreadcrumbsWrapper from "~/components/BreadcrumbsV2/BreadcrumbsWrapper";
import BreadcrumbElement from "~/components/BreadcrumbsV2/components/BreadcrumbElement";
import Button from "~/components/buttons/Button/Button";
import ButtonNewItem from "~/components/buttons/ButtonNewItem/ButtonNewItem";
import type { PopoverItem } from "~/components/buttons/ButtonWithPopoverDots.tsx";
import CompanyLogo from "~/components/CompanyLogo";
import ContentWrapper from "~/components/ContentWrapper";
import Headline from "~/components/Headline/Headline";
import SidebarBusyOverlay from "~/components/Sidebar/components/SidebarBusyOverlay";
import SidebarErrorOverlay from "~/components/Sidebar/components/SidebarErrorOverlay";
import { useAuth } from "~/contexts/AuthContext";
import type { InvoiceCommentWithDate } from "~/modules/billing/api/invoiceComment/invoiceCommentTypes.ts";
import { invoiceStatusId } from "~/modules/billing/api/invoiceStatus/invoiceStatusId.ts";
import type { InvoiceStatus } from "~/modules/billing/api/invoiceStatus/invoiceStatusIdTypes.ts";
import { getInvoiceTypeDisplayNameById, invoiceTypeId } from "~/modules/billing/api/invoiceType.ts";
import {
	advanceInvoiceStatus,
	revertInvoiceStatus,
} from "~/modules/billing/api/processedInvoice/processedInvoiceApiDispatchers.ts";
import {
	ALL_PROCESSED_INVOICES_QUERY_KEY,
	processedInvoiceQueries,
} from "~/modules/billing/api/processedInvoice/processedInvoiceQueries.ts";
import type {
	InvoiceDetails,
	InvoiceStatusId,
} from "~/modules/billing/api/processedInvoice/processedInvoiceTypes.ts";
import CreateInvoiceCommentSidebar
	from "~/modules/billing/components/InvoiceDetailsView/components/CreateInvoiceCommentSidebar/CreateInvoiceCommentSidebar.tsx";
import CreateReversalInvoiceSidebar
	from "~/modules/billing/components/InvoiceDetailsView/components/CreateReversalInvoiceSidebar/CreateReversalInvoiceSidebar.tsx";
import { CurrentInvoiceToDos } from "~/modules/billing/components/InvoiceDetailsView/components/CurrentInvoiceToDos.tsx";
import DeleteInvoiceCommentModal
	from "~/modules/billing/components/InvoiceDetailsView/components/DeleteInvoiceCommentModal";
import DeleteInvoiceSidebar
	from "~/modules/billing/components/InvoiceDetailsView/components/DeleteInvoiceSidebar/DeleteInvoiceSidebar.tsx";
import EInvoicePreviewModal
	from "~/modules/billing/components/InvoiceDetailsView/components/EInvoicePreviewModal/EInvoicePreviewModal.tsx";
import InvoiceAddressSection
	from "~/modules/billing/components/InvoiceDetailsView/components/InvoiceAddressSection/InvoiceAddressSection.tsx";
import InvoiceComment from "~/modules/billing/components/InvoiceDetailsView/components/InvoiceComment";
import InvoiceDeliveryPeriodSection
	from "~/modules/billing/components/InvoiceDetailsView/components/InvoiceDeliveryPeriodSection.tsx";
import InvoiceDescriptionSection
	from "~/modules/billing/components/InvoiceDetailsView/components/InvoiceDescriptionSection.tsx";
import InvoiceDetailsSection from "~/modules/billing/components/InvoiceDetailsView/components/InvoiceDetailsSection";
import InvoiceNumberHeader from "~/modules/billing/components/InvoiceDetailsView/components/InvoiceNumberHeader";
import InvoicePaymentSection
	from "~/modules/billing/components/InvoiceDetailsView/components/InvoicePaymentSection.tsx";
import {
	InvoiceQuotaDetailsSection,
	InvoiceQuotaSummarySection,
} from "~/modules/billing/components/InvoiceDetailsView/components/InvoiceQuotaDetailsSection.tsx";
import InvoiceRecipientSection
	from "~/modules/billing/components/InvoiceDetailsView/components/InvoiceRecipientSection.tsx";
import InvoiceStatusProgressBar
	from "~/modules/billing/components/InvoiceDetailsView/components/InvoiceStatusProgressBar/InvoiceStatusProgressBar.tsx";
import InvoiceTotalsSection from "~/modules/billing/components/InvoiceDetailsView/components/InvoiceTotalsSection.tsx";
import ReversalInvoiceCard from "~/modules/billing/components/InvoiceDetailsView/components/ReversalInvoiceCard";
import ReversedInvoiceCard from "~/modules/billing/components/InvoiceDetailsView/components/ReversedInvoiceCard";
import RevertInvoiceStatusModal
	from "~/modules/billing/components/InvoiceDetailsView/components/RevertInvoiceStatusModal/RevertInvoiceStatusModal.tsx";
import UpdateInvoiceAddressSidebar
	from "~/modules/billing/components/InvoiceDetailsView/components/UpdateInvoiceAddressSidebar/UpdateInvoiceAddressSidebar.tsx";
import UpdateInvoiceCommentSidebar
	from "~/modules/billing/components/InvoiceDetailsView/components/UpdateInvoiceCommentSidebar/UpdateInvoiceCommentSidebar.tsx";
import UpdateInvoiceDeliveryPeriodSidebar
	from "~/modules/billing/components/InvoiceDetailsView/components/UpdateInvoiceDeliveryPeriodSidebar/UpdateInvoiceDeliveryPeriodSidebar.tsx";
import UpdateInvoicePaymentInformationSidebar
	from "~/modules/billing/components/InvoiceDetailsView/components/UpdateInvoicePaymentInformationSidebar/UpdateInvoicePaymentInformationSidebar.tsx";
import UpdateInvoiceRecipientSidebar
	from "~/modules/billing/components/InvoiceDetailsView/components/UpdateInvoiceRecipientSidebar/UpdateInvoiceRecipientSidebar.tsx";
import UpdateInvoiceTotalSidebar
	from "~/modules/billing/components/InvoiceDetailsView/components/UpdateInvoiceTotalSidebar/UpdateInvoiceTotalSidebar.tsx";
import UpdateNonProjectInvoiceDescriptionSidebar
	from "~/modules/billing/components/InvoiceDetailsView/components/UpdateNonProjectInvoiceDescriptionSidebar/UpdateNonProjectInvoiceDescriptionSidebar.tsx";
import {
	getInvoiceDetailsBackPath,
	type ValidInvoiceDetailsNavigatedFromOrigin,
	validInvoiceDetailsNavigatedFromOrigins,
} from "~/modules/billing/components/InvoiceDetailsView/invoiceDetailsUtils.ts";
import AssignProcessedInvoiceToOrderSidebar
	from "~/modules/billing/components/InvoiceDetailsView/tempComponents/AssignProcessedInvoiceToOrderSidebar/AssignProcessedInvoiceToOrderSidebar";
import ExcelImportErrors from "~/modules/billing/components/InvoiceDetailsView/tempComponents/ExcelImportErrors";
import { wipStatuses } from "~/modules/billing/utils/billingUtils.ts";
import type { Project } from "~/modules/project/api/project/projectTypes.ts";
import type { QuotaWithRelations } from "~/modules/quota/api/quota/quotaTypes.ts";
import { invalidateQuotaQueries } from "~/modules/quota/utils/quotaCacheUtils.ts";
import { PermissionNames } from "~/types/entityNames.ts";
import { formatNumberToCurrency } from "~/utils/currencyUtils.ts";
import { formatSmartDateRange } from "~/utils/dateAndTimeUtils.ts";
import { byObjectProperty } from "~/utils/sortFunctions.ts";

type Props = {
	invoiceData: InvoiceDetails;
	invoiceStatuses: InvoiceStatus[];
	navigatedFrom?: ValidInvoiceDetailsNavigatedFromOrigin;
	navigatedFromProject?: Project;
	quota?: QuotaWithRelations
};

const sentAndPaidStatusIds = [invoiceStatusId.SentToClient, invoiceStatusId.Paid] as InvoiceStatusId[];

const InvoiceDetailsView: FunctionComponent<Props> = ({ invoiceData, invoiceStatuses, navigatedFrom, navigatedFromProject, quota }) => {
	const queryClient = useQueryClient();
	const { hasAnyPermission } = useAuth();
	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [serverErrorMessage, setServerErrorMessage] = useState<string>("");
	const userCanManageInvoices = hasAnyPermission([PermissionNames.CanManageInvoices]);

	const [invoiceCommentDataToUpdate, setInvoiceCommentDataToUpdate] = useState<InvoiceCommentWithDate | undefined>(undefined);
	const [invoiceCommentDataToDelete, setInvoiceCommentDataToDelete] = useState<InvoiceCommentWithDate | undefined>(undefined);
	const [statusIdToRevertTo, setStatusIdToRevertTo] = useState<InvoiceStatusId | undefined>(undefined);
	const [showAssignToOrderSidebar, setShowAssignToOrderSidebar] = useState<boolean>(false);
	const [showCreateInvoiceCommentSidebar, setShowCreateInvoiceCommentSidebar] = useState<boolean>(false);
	const [showCreateReversalInvoiceSidebar, setShowCreateReversalInvoiceSidebar] = useState<boolean>(false);
	const [showDeleteInvoiceSidebar, setShowDeleteInvoiceSidebar] = useState<boolean>(false);
	const [showEInvoiceSummaryModal, setShowEInvoiceSummaryModal] = useState<boolean>(false);
	const [showUpdateInvoiceAddressSidebar, setShowUpdateInvoiceAddressSidebar] = useState<boolean>(false);
	const [showUpdateDeliveryPeriodSidebar, setShowUpdateDeliveryPeriodSidebar] = useState<boolean>(false);
	const [showUpdateDescriptionSidebar, setShowUpdateDescriptionSidebar] = useState<boolean>(false);
	const [showUpdateInvoicePaymentInformationSidebar, setShowUpdateInvoicePaymentInformationSidebar] = useState<boolean>(false);
	const [showUpdateInvoiceTotalSidebar, setShowUpdateInvoiceTotalSidebar] = useState<boolean>(false);
	const [showUpdateRecipientInformationSidebar, setShowUpdateRecipientInformationSidebar] = useState<boolean>(false);

	const invoiceNumber = invoiceData.invoiceNumber ? invoiceData.invoiceNumber : "Rechnungsnummer tba";
	const isProjectInvoice = invoiceData.isProjectInvoice;
	const isReversalInvoice = invoiceData.invoiceTypeId === invoiceTypeId.ReversalInvoice;
	const isReversedInvoice = invoiceData.invoiceStatusId === invoiceStatusId.Reversed;
	const isAdvanceInvoice = invoiceData.invoiceTypeId === invoiceTypeId.AdvanceInvoice;
	const showsTrackedTotal = isProjectInvoice && !isAdvanceInvoice && !isReversalInvoice;

	const commentsSorted = useMemo(() => {
		if (invoiceData.comments) {
			return invoiceData.comments.map(comment => {
				return {
					...comment,
					createdAt: new Date(comment.createdAt),
				};
			}).sort(byObjectProperty("createdAt", "desc"));
		}
		return [];
	}, [invoiceData.comments]);

	// The back link and the breadcrumb return to the project the user came from. A quota invoice of another
	// project opens from the revenue tab of each project that the quota serves.
	const backProjectId = navigatedFromProject?.id ?? (invoiceData.projectId || undefined);
	const backProjectTitle = navigatedFromProject?.title ?? invoiceData.projectTitle;

	const Breadcrumbs = useMemo(() => {
		const path = getInvoiceDetailsBackPath(navigatedFrom, backProjectId);

		if (navigatedFrom === validInvoiceDetailsNavigatedFromOrigins.PROJECT_REVENUE_REPORT) {
			return <>
				<BreadcrumbElement text={backProjectTitle!}
								   url={path} />
				<BreadcrumbElement text="Umsatz" />
			</>;
		} else if (navigatedFrom === validInvoiceDetailsNavigatedFromOrigins.PROJECT_INVOICES) {
			return <>
				<BreadcrumbElement text={backProjectTitle!}
								   url={path} />
				<BreadcrumbElement text="Rechnungen" />
			</>;
		} else {
			return <BreadcrumbElement text="Rechnungen"
									  url={path} />;
		}
	}, [backProjectId, backProjectTitle, navigatedFrom]);


	const popoverItems = useMemo(() => {
		const items: PopoverItem[] = [];
		const { delete: deleteAction, reverse: reverseAction } = invoiceData.actions;
		// The backend decides whether the reversal is allowed. The status check only decides where the item shows.
		if (!isReversalInvoice && sentAndPaidStatusIds.includes(invoiceData.invoiceStatusId)) {
			items.push({
				label: "Rechnung stornieren",
				onClick: () => setShowCreateReversalInvoiceSidebar(true),
				icon: ArrowUturnLeftIcon,
				iconColorClassName: "fill-danger-500",
				disabled: !reverseAction.allowed,
				disabledReason: reverseAction.reason,
			});
		}

		items.push({
			label: "Rechnung löschen",
			onClick: () => setShowDeleteInvoiceSidebar(true),
			icon: TrashIcon,
			iconColorClassName: "text-danger-500",
			disabled: !deleteAction.allowed,
			disabledReason: deleteAction.reason,
		});

		if(invoiceData.invoiceStatusId === invoiceStatusId.Paid) {
			items.push({
				label: "E-Rechnung herunterladen",
				onClick: () => setShowEInvoiceSummaryModal(true),
				icon: ArrowDownTrayIcon,
				iconColorClassName: "fill-primary-800",
			});
		}


		return items;
	}, [invoiceData.actions, invoiceData.invoiceStatusId, isReversalInvoice]);

	const handleStatusClick = useCallback(async (statusId: InvoiceStatusId, action: "advance" | "revert") => {
		if (!userCanManageInvoices) return;
		try {

			if (action === "advance") {
				setIsBusy(true);
				await advanceInvoiceStatus(invoiceData.id, statusId);
				// The quota view shows the status of an advance invoice and of an attached invoice.
				if (invoiceData.quota) void invalidateQuotaQueries(queryClient);
			} else {
				if (wipStatuses.includes(statusId) && wipStatuses.includes(invoiceData.invoiceStatusId)) {
					setIsBusy(true);
					await revertInvoiceStatus(invoiceData.id, statusId);
					if (invoiceData.quota) void invalidateQuotaQueries(queryClient);
				} else {
					setStatusIdToRevertTo(statusId);
				}
			}
			await queryClient.refetchQueries({ queryKey: processedInvoiceQueries.details(invoiceData.id).queryKey });
			queryClient.invalidateQueries({ queryKey: ALL_PROCESSED_INVOICES_QUERY_KEY });

			if (invoiceData.projectId) {
				queryClient.invalidateQueries({ queryKey: processedInvoiceQueries.byProject(invoiceData.projectId).queryKey });
			}
		} catch (error) {
			console.log(error);
			setServerErrorMessage(getValidationMessage(error, ["invoiceId"]) ?? t("form:unexpectedError"));
		} finally {
			setIsBusy(false);
		}

	}, [invoiceData.id, invoiceData.invoiceStatusId, invoiceData.projectId, invoiceData.quota, queryClient, userCanManageInvoices]);

	const excelErrors = invoiceData.excelRow?.errors ? invoiceData.excelRow?.errors : [];
	const hasMissingOrderId = invoiceData.isProjectInvoice && invoiceData.orders.length === 0;
	const showTodoSection = invoiceData.invoiceStatusId !== invoiceStatusId.Paid && !isReversedInvoice;
	const isSelfBilledInvoice = invoiceData.invoiceTypeId === invoiceTypeId.SelfBilledInvoice;
	const companyAvatarImage = invoiceData.companyAvatarImage || quota?.companyAvatarImage;
	return <div className="auto-grid-2-rows relative grid h-screen">
		{serverErrorMessage && <SidebarErrorOverlay title="Update fehlgeschlagen"
													onDismiss={() => setServerErrorMessage("")}>{serverErrorMessage}</SidebarErrorOverlay>}
		{isBusy && <SidebarBusyOverlay />}

		<div className="flex w-full flex-col bg-white">
			<ContentWrapper className="w-full pb-4">
				<BreadcrumbsWrapper>
					{Breadcrumbs}
					<BreadcrumbElement text={invoiceNumber} />
				</BreadcrumbsWrapper>
				<div className="flex w-full items-center gap-x-3 pt-2">
					{companyAvatarImage && <CompanyLogo image={companyAvatarImage}
														sizeClassName="size-10" />}
					<div className="flex w-full flex-col">
						<div className="flex w-full items-center justify-between gap-x-2">
							<InvoiceNumberHeader>{invoiceNumber}</InvoiceNumberHeader>
							<div className="flex shrink-0 items-center">
								<Headline type="h2"
										  className={invoiceData.totalAmountCents < 0 ? "text-secondary-500" : ""}>{formatNumberToCurrency(invoiceData.totalAmountCents) + " " + invoiceData.currencySymbol}</Headline>

							</div>
						</div>
						<div className="flex w-full items-center justify-between">
							<Headline type="h5"
									  className="flex items-center gap-x-1">{getInvoiceTypeDisplayNameById(invoiceData.invoiceTypeId)}
								{!isProjectInvoice &&
									<div className="font-normal text-gray-400">(nicht projektbezogen)</div>}
							</Headline>
							<div className="flex items-center">
								<Headline type="h5"
										  className="flex items-center gap-x-1">
									{formatSmartDateRange(new Date(invoiceData.deliveryStartDate), new Date(invoiceData.deliveryEndDate), {
										month: "MMMM",
										year: " yyyy",
									})}</Headline>
							</div>
						</div>
					</div>
					{userCanManageInvoices && popoverItems && <div className="self-start pt-2"><ButtonWithPopoverDots
						items={popoverItems}
						theme="dark"
					/></div>}
				</div>
				<div className="mt-4">
					<InvoiceStatusProgressBar statuses={invoiceStatuses}
											  advanceAction={invoiceData.actions.advance}
											  onStatusClick={userCanManageInvoices ? handleStatusClick : undefined}
											  isSelfBilledInvoice={isSelfBilledInvoice}
											  recipientIsSet={!!invoiceData.recipientInformation}
											  revertAction={invoiceData.actions.revert}
											  revertBeforeSentAction={invoiceData.actions.revertBeforeSent}
											  currentStatusId={invoiceData.invoiceStatusId} />
				</div>
			</ContentWrapper>
		</div>
		<ContentWrapper className="grid w-full grid-cols-5 content-start gap-4 overflow-y-auto pb-10 pt-4">
			{showTodoSection && <div className="col-span-5">
				<CurrentInvoiceToDos invoiceData={invoiceData}
									 canManageInvoices={userCanManageInvoices}
									 onDownloadEInvoiceClick={() => setShowEInvoiceSummaryModal(true)}
									 onSetInvoiceRecipientClick={() => setShowUpdateRecipientInformationSidebar(true)}
									 setIsBusy={setIsBusy}
									 setServerErrorMessage={setServerErrorMessage} />
			</div>}
			{excelErrors.length > 0 && <ExcelImportErrors excelErrors={excelErrors}
														  rowNumber={invoiceData.excelRowNumber} />}
			<div className="col-span-3 space-y-4">
				{/* An advance invoice shows its quota in place of the invoice details. An invoice attached to an
				invoice-backed quota keeps its invoice details. */}
				{invoiceData.quota?.type !== "advance" && isProjectInvoice && <InvoiceDetailsSection
					invoiceData={invoiceData}
					onUpdateClick={hasMissingOrderId && userCanManageInvoices ? () => setShowAssignToOrderSidebar(true) : undefined} />}

				{!isProjectInvoice && <InvoiceDescriptionSection invoiceData={invoiceData}
																 onUpdateClick={() => setShowUpdateDescriptionSidebar(true)} />}

				{quota && <InvoiceQuotaDetailsSection invoiceId={invoiceData.id}
													  quota={quota} />}
				{invoiceData.quota && !quota && <InvoiceQuotaSummarySection quota={invoiceData.quota} />}

				<div className="flex flex-col gap-y-4">
					<div className="flex items-center justify-between pl-4">
						<Headline type="h4">Kommentare / Bemerkungen</Headline>
						{commentsSorted.length > 0 && <Button theme="none"
															  size="xs"
															  onClick={() => setShowCreateInvoiceCommentSidebar(true)}>
							Neuer Kommentar</Button>}
					</div>
					{commentsSorted.length > 0 && commentsSorted.map((comment) => {
						return <InvoiceComment key={comment.id}
											   onUpdateClick={() => setInvoiceCommentDataToUpdate(comment)}
											   onDeleteClick={() => setInvoiceCommentDataToDelete(comment)}
											   commentData={comment} />;
					})}
					{commentsSorted.length === 0 && <ButtonNewItem theme="inline"
																   size="sm"
																   onClick={() => setShowCreateInvoiceCommentSidebar(true)}>
						Neuer Kommentar</ButtonNewItem>}
				</div>
			</div>
			<div className="col-span-2 space-y-4">
				<InvoiceDeliveryPeriodSection invoiceData={invoiceData}
											  onUpdateClick={() => setShowUpdateDeliveryPeriodSidebar(true)} />
				<InvoicePaymentSection invoiceData={invoiceData}
									   onUpdateClick={() => setShowUpdateInvoicePaymentInformationSidebar(true)} />
				<InvoiceRecipientSection invoiceData={invoiceData}
										 onUpdateClick={() => setShowUpdateRecipientInformationSidebar(true)} />
				{!isSelfBilledInvoice && <InvoiceAddressSection invoice={invoiceData}
																onUpdateClick={() => setShowUpdateInvoiceAddressSidebar(true)}
				/>}
				{isReversalInvoice && <ReversedInvoiceCard reversedInvoiceId={invoiceData.reversedInvoiceId!} />}
				{isReversedInvoice && <ReversalInvoiceCard reversalInvoiceData={invoiceData.reversalInvoiceCoreData} />}

				<InvoiceTotalsSection currencySymbol={invoiceData.currencySymbol}
									  editTotalsAction={invoiceData.actions.editTotals}
									  onEditClick={userCanManageInvoices ? () => setShowUpdateInvoiceTotalSidebar(true) : undefined}
									  showTravelCosts={!isAdvanceInvoice}
									  subtotalCents={invoiceData.subtotalCents}
									  totalAmountCents={invoiceData.totalAmountCents}
									  totalTrackedCents={showsTrackedTotal ? invoiceData.totalTrackedCents : undefined}
									  travelCostCentsBeforeVat={invoiceData.travelCostCentsBeforeVat}
									  vatPercentage={invoiceData.vatPercentage}
									  vatTotalCents={invoiceData.vatTotalCents} />
			</div>
		</ContentWrapper>

		<CreateInvoiceCommentSidebar invoiceId={invoiceData.id}
									 isOpen={showCreateInvoiceCommentSidebar}
									 onClose={() => setShowCreateInvoiceCommentSidebar(false)}
									 projectId={invoiceData.projectId} />
		<UpdateInvoiceCommentSidebar invoiceId={invoiceData.id}
									 isOpen={!!invoiceCommentDataToUpdate}
									 onClose={() => setInvoiceCommentDataToUpdate(undefined)}
									 projectId={invoiceData.projectId}
									 comment={invoiceCommentDataToUpdate} />

		<DeleteInvoiceCommentModal invoiceId={invoiceData.id}
								   isOpen={!!invoiceCommentDataToDelete}
								   onClose={() => setInvoiceCommentDataToDelete(undefined)}
								   projectId={invoiceData.projectId}
								   comment={invoiceCommentDataToDelete} />

		<UpdateInvoiceDeliveryPeriodSidebar isOpen={showUpdateDeliveryPeriodSidebar}
											onClose={() => setShowUpdateDeliveryPeriodSidebar(false)}
											invoiceData={invoiceData} />
		<UpdateNonProjectInvoiceDescriptionSidebar invoiceData={invoiceData}
												   isOpen={showUpdateDescriptionSidebar}
												   onClose={() => setShowUpdateDescriptionSidebar(false)} />

		<UpdateInvoicePaymentInformationSidebar invoiceData={invoiceData}
												isOpen={showUpdateInvoicePaymentInformationSidebar}
												onClose={() => setShowUpdateInvoicePaymentInformationSidebar(false)} />

		<CreateReversalInvoiceSidebar isOpen={showCreateReversalInvoiceSidebar}
									  onClose={() => setShowCreateReversalInvoiceSidebar(false)}
									  originalInvoiceData={invoiceData} />
		<UpdateInvoiceTotalSidebar invoiceData={invoiceData}
								   isOpen={showUpdateInvoiceTotalSidebar}
								   onClose={() => setShowUpdateInvoiceTotalSidebar(false)} />

		<UpdateInvoiceAddressSidebar invoiceData={invoiceData}
									 isOpen={showUpdateInvoiceAddressSidebar}
									 onClose={() => setShowUpdateInvoiceAddressSidebar(false)} />

		<RevertInvoiceStatusModal invoiceData={invoiceData}
								  isOpen={!!statusIdToRevertTo}
								  newStatusId={statusIdToRevertTo}
								  onClose={() => setStatusIdToRevertTo(undefined)} />

		<UpdateInvoiceRecipientSidebar
			invoiceData={invoiceData}
			isOpen={showUpdateRecipientInformationSidebar}
			onClose={() => setShowUpdateRecipientInformationSidebar(false)}
		/>

		{hasMissingOrderId && <AssignProcessedInvoiceToOrderSidebar isOpen={showAssignToOrderSidebar}
																	onClose={() => setShowAssignToOrderSidebar(false)}
																	invoiceData={invoiceData} />}
		<DeleteInvoiceSidebar invoiceData={invoiceData}
							  isOpen={showDeleteInvoiceSidebar}
							  redirectToInvoicesTab={navigatedFrom}
							  redirectToProjectId={backProjectId}
							  onClose={() => setShowDeleteInvoiceSidebar(false)} />

		<EInvoicePreviewModal invoiceId={invoiceData.id}
							  onClose={() => setShowEInvoiceSummaryModal(false)}
							  isOpen={showEInvoiceSummaryModal} />
	</div>;
};

export default InvoiceDetailsView;